import React from "react";
import { useTranslate } from "@pankod/refine-core";
import { Card, Form, Tag, Button, Space } from "@pankod/refine-antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { LevelType } from "interfaces";
import { renderLevelSummary } from "./levelSummary";

interface Props {
  id: string;               // stable DnD id (decoupled from form-list index)
  fieldName: number;        // antd Form.List field.name (index into the levels array)
  displayedNumber: number;  // 1-based badge — derived from current order, not stored
  onEdit: () => void;
  onRemove: () => void;
}

/**
 * Collapsed grid card for one thematic level. Renders a one-line summary of
 * the current config (live from form state via Form.useWatch) plus drag handle
 * and Edit / Delete buttons. The full per-level form lives in the Modal
 * managed by LevelsEditor — this card never expands inline.
 */
export const SortableLevelCard: React.FC<Props> = ({
  id,
  fieldName,
  displayedNumber,
  onEdit,
  onRemove,
}) => {
  const t = useTranslate();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Live read of this level's type and config so the summary updates as soon
  // as the modal saves.
  const levelType: LevelType | undefined = Form.useWatch(
    ["thematic", "levels", fieldName, "level_type"]
  );
  const config: any = Form.useWatch(
    ["thematic", "levels", fieldName, "config"]
  );
  // Minigame uses `config_raw` (JSON string from the textarea) in form state;
  // `config` is only populated when the serializer parses it on submit. For
  // the live summary we parse config_raw ourselves so the card preview shows
  // the actual JSON instead of `{}`.
  const configRaw: string | undefined = Form.useWatch(
    ["thematic", "levels", fieldName, "config_raw"]
  );
  let effectiveConfig: any = config;
  if (levelType === "minigame" && typeof configRaw === "string" && configRaw.trim()) {
    try {
      effectiveConfig = JSON.parse(configRaw);
    } catch {
      // leave effectiveConfig as-is; the modal's validator would have flagged invalid JSON
    }
  }

  return (
    <div ref={setNodeRef} style={style}>
      <Card
        size="small"
        title={
          <Space size={6}>
            <span
              {...attributes}
              {...listeners}
              style={{
                cursor: "grab",
                padding: "0 4px",
                color: "#999",
                userSelect: "none",
                fontSize: 16,
              }}
            >
              ☰
            </span>
            <strong>#{displayedNumber}</strong>
            {levelType && (
              <Tag style={{ margin: 0 }}>
                {t(`event.thematic.level_type_${levelType}`)}
              </Tag>
            )}
          </Space>
        }
        actions={[
          <Button
            key="edit"
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={onEdit}
          />,
          <Button
            key="delete"
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={onRemove}
          />,
        ]}
      >
        {renderLevelSummary({ level_type: levelType, config: effectiveConfig })}
      </Card>
    </div>
  );
};
